import { useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AuthLayout } from '@/bundle/Auth/ui/AuthLayout/AuthLayout';
import { sendTwoFACode, sendTwoFA } from './api';
import { TwoFAForm } from './ui/TwoFAForm/TwoFAForm';
import { useAuthNavigateToStep } from '../../hooks/useAuthNavigateToStep';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { AdminOnboardingStepper } from '../../AdminOnboardingFlow/ui/AdminOnboardingStepper';
import { useAdminOnboardingFlowFromAuthToken } from '@/bundle/shared/hooks/useAdminOnboardingFlowFromAuthToken';

export const TwoFAPage = () => {
  const navigateToStep = useAuthNavigateToStep();
  const { isOnboardingFlow, signUpStep } = useAdminOnboardingFlowFromAuthToken();
  const twoFAFormRef = useRef(null);

  const {
    mutate: sendTwoFAMutate,
    data: sendTwoFAData,
    isPending: isSendTwoFAPending,
  } = useMutation({
    mutationKey: ['send_two_fa'],
    mutationFn: (twoFACode: string) => {
      return sendTwoFA(twoFACode);
    },
    onSuccess(response) {
      if (response?.error) return;

      amplitudeService.logEvent(AMPLITUDE_EVENTS.TwoFaCodeCheckSuccess);

      return navigateToStep(response?.body);
    },
    onSettled() {
      twoFAFormRef?.current?.resetCodeChanged();
    },
  });

  const {
    mutate: sendCodeMutate,
    data: sendCodeData,
    isPending: isSendCodePending,
  } = useMutation({
    mutationKey: ['send_code'],
    mutationFn: () => {
      return sendTwoFACode();
    },
    onSuccess(response) {
      const retryAfter = response?.headers?.retryAfter;

      twoFAFormRef?.current?.resetTimer(retryAfter);

      if (response?.error) return;
    },
  });

  const confirmTwoFA = (twoFACode: string) => {
    if (isSendTwoFAPending) return;

    sendTwoFAMutate(twoFACode);
  };

  return (
    <AuthLayout banner={isOnboardingFlow && <AdminOnboardingStepper signUpStep={signUpStep} />}>
      <TwoFAForm
        ref={twoFAFormRef}
        isTwoFALoading={isSendTwoFAPending}
        isSendCodeLoading={isSendCodePending}
        twoFAError={sendTwoFAData?.error}
        sendCodeError={sendCodeData?.error}
        onConfirm={confirmTwoFA}
        onResend={sendCodeMutate}
      />
    </AuthLayout>
  );
};
